import css from '../view.module.css';
import React, {
  Dispatch,
  SetStateAction,
  useState,
  forwardRef,
  useRef,
  useEffect,
} from 'react';
import moment from 'moment';
import DeleteNoteModal from './DeleteNoteModal';
import EditNotesForm from './EditNotesForm';
import { useService } from '../../../../../services/services';
import { useTranslation } from 'react-i18next';
import { ButtonComponent } from '../../../../../components/form/elements/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router';
import { faCalendarDay } from '@fortawesome/pro-solid-svg-icons';
import DatePicker from 'react-datepicker';
import GroupAnimalNote from '../../../../../interfaces/group-animal-note';

interface Props {
  update: boolean;
  setUpdate: Dispatch<SetStateAction<boolean>>;
  animalGroupId: string;
  apiRoutePrefix: string;
}

export default function NotesBlocks({
  update,
  setUpdate,
  animalGroupId,
  apiRoutePrefix,
}: Props) {
  const { t } = useTranslation();
  const { farmId } = useParams<{ farmId: string }>();
  const [chosenNoteId, setChosenNoteId] = useState('');
  const [chosenEditNoteId, setChosenEditNoteId] = useState<string>('');
  const { groupAnimalService } = useService();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals';
  const [fromDate, setFromDate] = useState<Date>(null);
  const [toDate, setToDate] = useState<Date>(null);
  const [notes, setNotes] = useState<GroupAnimalNote[]>(null);

  const refCustomInput = useRef();
  useEffect(() => {
    groupAnimalService
      .notes(farmId, animalGroupId, apiRoutePrefix)
      .subscribe((response) => {
        setNotes(response);
      });
  }, [update, groupAnimalService, farmId, animalGroupId, apiRoutePrefix]);

  const CustomInput = forwardRef((props) => {
    return (
      <label>
        <input {...props} autoComplete="off" />
        <FontAwesomeIcon icon={faCalendarDay} />
      </label>
    );
  });

  const nextDayDate = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  };

  return (
    <div className={css['notes']}>
      <h1 className={css['notes-select-text']}>
        {t(`${transKeyPrefix}.select_notes`)}
      </h1>
      <div className="react-datepicker-container">
        <div className={css['notes-filter-date']}>
          <DatePicker
            selected={fromDate}
            locale="lv-LV"
            dateFormat="dd.MM.yyyy"
            customInput={<CustomInput ref={refCustomInput} />}
            placeholderText={t(`${transKeyPrefix}.date_from`)}
            onChange={(date) => setFromDate(date)}
            name="from"
          />
          <span className={css['date-picker-left']}>
            <DatePicker
              locale="lv-LV"
              dateFormat="dd.MM.yyyy"
              selected={toDate}
              onChange={(date) => {
                setToDate(date);
              }}
              name="to"
              placeholderText={t(`${transKeyPrefix}.date_to`)}
              customInput={<CustomInput ref={refCustomInput} />}
            />
          </span>
        </div>
      </div>

      {notes &&
        notes.map(
          (el) =>
            (fromDate == null ||
              new Date(el['created_at']) >= new Date(fromDate)) &&
            (toDate == null ||
              new Date(el['created_at']) < nextDayDate(toDate)) && (
              <div key={el.id}>
                {chosenEditNoteId === el.id ? (
                  <EditNotesForm
                    notes={el}
                    setChosenEditNoteId={setChosenEditNoteId}
                    setChosenNoteId={setChosenNoteId}
                    update={update}
                    setUpdate={setUpdate}
                    animalGroupId={animalGroupId}
                    apiRoutePrefix={apiRoutePrefix}
                  />
                ) : (
                  <div>
                    <h1 className={css['note-date']}>
                      {moment(el.created_at).format('DD/MM/YYYY')}
                    </h1>
                    <div
                      className={css['lined-block']}
                      onClick={() => setChosenNoteId(el.id)}
                    >
                      <h1 className={css['note-title']}>{el.title ?? ''}</h1>
                      <h1 className={css['note-content']}>
                        {el.content ?? ''}
                      </h1>
                    </div>
                  </div>
                )}
                {el.id === chosenNoteId &&
                  chosenEditNoteId !== chosenNoteId && (
                    <div className={css['notes-action']}>
                      <ButtonComponent
                        name="edit"
                        label={t('edit')}
                        click={() => {
                          setChosenEditNoteId(el.id);
                        }}
                      />
                      <DeleteNoteModal
                        notesId={chosenNoteId}
                        update={update}
                        setUpdate={setUpdate}
                        animalGroupId={animalGroupId}
                        apiRoutePrefix={apiRoutePrefix}
                      />
                    </div>
                  )}
              </div>
            )
        )}
    </div>
  );
}
